import { ArrowRightIcon, PlusIcon, UserIcon, UsersIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { Button, ButtonGroup, TextButton, useBreakpoint } from 'slab'

import { AllowanceBanner } from '../components/AllowanceBanner'
import { CoveredParticipantCard } from '../components/CoveredParticipantCard'
import { OnboardingClearEnrollmentsModal } from '../components/OnboardingClearEnrollmentsModal'
import { SelfParticipantCard } from '../components/SelfParticipantCard'
import { useOnboarding } from '../OnboardingContext'
import { type StepComponentProps } from '../types'

export function ViewAllowancesPage ({
  navigateToNextStep,
  navigateToPreviousStep,
  navigateToSpecificStep,
}: StepComponentProps) {
  const { breakpointIsAtLeast } = useBreakpoint()
  // Dependents
  const { additionalParticipants, onboardingData } = useOnboarding()
  const [addDependentOpen, setAddDependentOpen] = useState(false) // Using the Add dependent card
  // This should always match the number of CoveredParticipantCards showing
  const [dependentsInfoSaved, setDependentsInfoSaved] = useState<boolean[]>(initAdditionalParticipantsInfoSaved())
  // "Changing this will empty your cart" modal
  const [showModal, setShowModal] = useState(false)
  const [modalSubmitted, setModalSubmitted] = useState(false)

  // Maps additionalParticipants to a boolean[] of whether the participant has any missing fields
  // Used to sync the dependentsInfoSaved state with the additionalParticipants
  function initAdditionalParticipantsInfoSaved (): boolean[] {
    if (!additionalParticipants?.data?.length) return []

    return additionalParticipants.data.map((participant) => {
      const allowanceMissingFields = onboardingData?.missing_fields.allowance
      const hasMissingFields = allowanceMissingFields?.some(
        field => field.includes(`additional_participant.${participant.id}`)
      )
      return !hasMissingFields // true if no missing fields, false otherwise
    })
  }

  // Used to disable the Next button
  const canNavigateToNextStep = useMemo(() => {
    const allowanceMissingFields = onboardingData?.missing_fields.allowance
    if ((!allowanceMissingFields || allowanceMissingFields.length === 0) &&
      dependentsInfoSaved.every((item) => item === true)) { // All dependents are saved
      return true
    }
    return false
  }, [dependentsInfoSaved, onboardingData])

  // Check if there's already a spouse among existing participants, used to disable the spouse radio
  const hasExistingSpouse = additionalParticipants?.data?.some(
    (participant) => participant.relationship === 'spouse'
  ) || false

  // === Handlers ===

  const onTriggerModal = () => {
    setShowModal(true)
  }

  // Sync dependentsInfoSaved with additionalParticipants when dependent removed
  useEffect(() => {
    setDependentsInfoSaved(initAdditionalParticipantsInfoSaved())
  }, [additionalParticipants?.data, onboardingData?.missing_fields?.allowance])

  // Sync dependentsInfoSaved with Add dependent card opened/closed
  useEffect(() => {
    // When Add dependent card is opened, add a dependentInfoSaved item : false
    if (addDependentOpen) {
      setDependentsInfoSaved(dependentsInfoSaved => [...dependentsInfoSaved, false])
    } else {
    // Remove the dependentInfoSaved item when Cancel button clicked
      setDependentsInfoSaved(dependentsInfoSaved => dependentsInfoSaved.slice(0, -1))
    }
  }, [addDependentOpen])

  // Passed to Edit dependent card to tell this page if any info isn't saved.
  // "Saved" meaning that additionalParticipants is up-to-date with what is in the form for that dependent
  // If any info isn't saved, we disable the Next button
  const onDependentInfoSaved = (index: number, isSaved: boolean) => {
    const nextDependentInfoSaved = dependentsInfoSaved.map((item, i) => {
      if (i === index) {
        return isSaved
      }
      return item
    })
    setDependentsInfoSaved(nextDependentInfoSaved)
  }

  // === Render ===

  // Buttons
  function renderButtons () {
    const declineButton = (
      <TextButton
        color='purple'
        onClick={() => {
          navigateToSpecificStep?.('opted_out')
        }}
      >
        Decline your coverage
      </TextButton>
    )

    const backButton = (
      <Button
        variant='secondary'
        onClick={() => {
          navigateToPreviousStep?.()
        }}
      >
        Back
      </Button>
    )

    const nextButton = (
      <Button
        rightIcon={ArrowRightIcon}
        variant='primary'
        disabled={!canNavigateToNextStep}
        onClick={() => {
          navigateToNextStep()
        }}
      >
        Next
      </Button>
    )

    if (breakpointIsAtLeast('xs')) {
      return (
        <div className='flex justify-between items-center'>
          {declineButton}
          <ButtonGroup size='large'>
            {backButton}
            {nextButton}
          </ButtonGroup>
        </div>
      )
    }

    return (
      <div className='flex flex-col gap-4'>
        <ButtonGroup size='large' layout='vertical'>
          {nextButton}
          {backButton}
        </ButtonGroup>
        {declineButton}
      </div>
    )
  }

  // ViewAllowancesPage
  return (
    <div
      data-testid='ViewAllowancesPage'
      className='flex flex-col gap-y-10 max-w-[44rem]'
    >
      <h1 className='heading-lg text-gray-800'>Generate your budget</h1>
      <AllowanceBanner />
      {/* Your details */}
      <section>
        <div className='flex items-center gap-x-2 body-bold text-gray-800 mb-4'>
          <UserIcon size={14} />
          <span>Your details</span>
        </div>
        <SelfParticipantCard />
      </section>
      {/* Dependents */}
      <section>
        <div className='flex items-center justify-between text-gray-800 mb-4'>
          <div className='flex items-center gap-x-2 body-bold'>
            <UsersIcon size={14} />
            <span>Dependents</span>
          </div>
          {/* Add dependent button */}
          <Button
            leftIcon={PlusIcon}
            size='large'
            variant='tertiary'
            onClick={() => {
              setAddDependentOpen(true)
            }}
            disabled={addDependentOpen}
          >
            Add dependent
          </Button>
        </div>
        {additionalParticipants?.data?.length === 0 && !addDependentOpen && (
          <div
            data-testid='add-dependent-placeholder'
            className='p-4 body text-center text-gray-500 rounded-lg border border-gray-400 border-dashed'
          >
            Click "Add dependent" to add a spouse or child to your insurance
            plan.
          </div>
        )}
        {/* Dependents cards */}
        <div className='flex flex-col gap-y-4'>
          {/* Existing dependents -- Edit dependent cards */}
          {additionalParticipants?.data?.map((participant, index) => (
            <CoveredParticipantCard
              key={participant.id}
              modalClosed={!showModal}
              participant={participant}
              onTriggerModal={onTriggerModal}
              modalSubmitted={modalSubmitted}
              onInfoSaved={(isSaved) => { // Used to disable the Next button if any info isn't saved
                onDependentInfoSaved(index, isSaved)
              }}
              hasExistingSpouse={hasExistingSpouse}
            />
          ))}
          {/* Add dependent card -- once the dependent is saved, re-renders as an Edit dependent card */}
          {addDependentOpen && (
            <CoveredParticipantCard
              onTriggerModal={onTriggerModal}
              modalSubmitted={modalSubmitted}
              modalClosed={!showModal}
              hasExistingSpouse={hasExistingSpouse}
              handlePostAction={() => {
                setAddDependentOpen(false)
              }}
            />
          )}
        </div>
      </section>
      <OnboardingClearEnrollmentsModal
        isModalOpen={showModal}
        onClose={() => {
          setShowModal(false)
        }}
        onSubmit={() => {
          setModalSubmitted(true)
          setShowModal(false)
        }}
        description='Dependent information can impact plan cost and availability, so making a change here will remove any plans currently in your cart'
      />
      {renderButtons()}
    </div>
  )
}
